import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Calendar, Clock, Tag, ArrowLeft, ArrowRight, Twitter, Linkedin } from "lucide-react";
import api from "../../lib/api";
import AuthorAvatar from "../../components/AuthorAvatar";
import SEO from "../../components/SEO";

/**
 * Public author profile page — `/blog/author/:slug`.
 * Shows the author's photo, role, bio, social links, and every post they've
 * written. Critical for Google E-E-A-T (Experience, Expertise, Authority,
 * Trust) ranking signals: search engines reward content with attributable
 * human experts who have a stable URL to point at.
 */
export default function BlogAuthor() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/blog/authors/${slug}`)
      .then((r) => {
        if (cancelled) return;
        setData(r.data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) setNotFound(true);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Author not found</h1>
          <button
            onClick={() => navigate("/blog")}
            className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 hover:text-emerald-900"
          >
            <ArrowLeft className="w-4 h-4" /> Back to blog
          </button>
        </div>
      </div>
    );
  }

  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  const { author, posts } = data;

  const origin = typeof window !== "undefined" ? window.location.origin : "https://realtyvaultmgr.com";
  const profileJsonLd = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    mainEntity: {
      "@type": "Person",
      name: author.name,
      jobTitle: author.role,
      description: author.bio,
      image: author.avatar_url?.startsWith("http") ? author.avatar_url : `${origin}${author.avatar_url}`,
      sameAs: [author.twitter, author.linkedin].filter(Boolean),
    },
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title={`${author.name} — Author`}
        description={author.bio || `Posts by ${author.name} on the RealtyVault Manager blog — commercial real-estate strategy, ops, and finance.`}
        path={`/blog/author/${slug}`}
        type="profile"
        jsonLd={profileJsonLd}
      />
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-bold text-lg text-slate-900 tracking-tight" style={{ fontFamily: "Outfit" }}>
            RealtyVault Manager
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/blog" className="text-slate-600 hover:text-slate-900">Blog</Link>
            <Link to="/login" className="text-slate-600 hover:text-slate-900">Sign in</Link>
            <Link to="/signup" className="bg-slate-900 text-white px-3 py-1.5 rounded-sm font-semibold text-xs hover:bg-slate-700">Start free trial</Link>
          </nav>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-900 mb-6">
          <ArrowLeft className="w-3.5 h-3.5" /> All articles
        </Link>

        <div className="bg-white border border-slate-200 rounded-sm p-6 mb-10 flex items-start gap-5" data-testid="author-profile-card">
          <AuthorAvatar author={author} size="lg" />
          <div className="flex-1 min-w-0">
            <div className="overline text-emerald-700">{author.role}</div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }}>
              {author.name}
            </h1>
            {author.bio && <p className="text-sm text-slate-600 mt-2">{author.bio}</p>}
            {(author.twitter || author.linkedin) && (
              <div className="flex items-center gap-2 mt-3">
                {author.twitter && (
                  <a href={author.twitter} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-slate-900">
                    <Twitter className="w-4 h-4" />
                  </a>
                )}
                {author.linkedin && (
                  <a href={author.linkedin} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-slate-900">
                    <Linkedin className="w-4 h-4" />
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4">
          {posts.length} article{posts.length === 1 ? "" : "s"}
        </h2>
        <ul className="space-y-6" data-testid="author-post-list">
          {posts.map((p) => (
            <li
              key={p.slug}
              className="bg-white border border-slate-200 hover:border-slate-900 rounded-sm p-5 transition-colors"
            >
              <Link to={`/blog/${p.slug}`}>
                <h3 className="text-xl font-bold tracking-tight text-slate-900 hover:text-emerald-700 transition-colors" style={{ fontFamily: "Outfit" }}>
                  {p.title}
                </h3>
                <p className="text-sm text-slate-600 mt-1.5">{p.description}</p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 mt-2.5">
                  <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(p.date)}</span>
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {p.reading_time_min} min</span>
                </div>
                {p.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2.5">
                    {p.tags.map((t) => (
                      <span key={t} className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-widest text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-sm font-semibold">
                        <Tag className="w-2.5 h-2.5" /> {t}
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                  Read article <ArrowRight className="w-3 h-3" />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </main>

      <footer className="border-t border-slate-200 bg-white mt-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 text-xs text-slate-500 flex flex-wrap items-center justify-between gap-2">
          <span>© 2026 RealtyVault Manager</span>
          <Link to="/" className="hover:text-slate-900">Home</Link>
        </div>
      </footer>
    </div>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso + "T00:00:00Z").toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch { return iso; }
}
